import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { get as _get } from 'lodash';

import { GetISODateFromQuick } from '../../../../libraries/helpers';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';
import MetadataService from '../../../../data/metadata-service';
import DataContext from '../../../../data/data-context.js';

import _AssetSelector from '../../../../components/filter/asset-selector';
import FilterBuilder from '../../../../components/filter/filterbuilder';
import SeaUrchin from '../../../../components/charts/seaurchin';
import DateRange from './../components/date-range';
import { VerifyDates } from '../../widget';

const useStyles = makeStyles(theme => ({
    root: {
    },
    // editor classes
    editorRoot: {
        position: 'relative',
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    tagsButton: {
        width: 99,
    },
    filterText: {
        marginLeft: 10,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 12 }
    : { minW: 3, minH: 10 };

// don't display until size-me can figure out what our size is
const MtdtIPActivityWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MtdtIPActivityWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const AssetSelector = props => {
    return <_AssetSelector { ...props } placement='top-start'/>;
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [params, setParams] = useState({});
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const mdPerms = Auth.permissions.get('Metadata');

    const handleSeaurchinFilter = f => {
        if (!f) return;
        f = { operator: 'And', nodes: [f] };
        if (_get(props.data, 'settings.json_filter.nodes', []).length > 0) {
            f.nodes.push(props.data.settings.json_filter);
        }
        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(f),
            sources: ['conn'],
        };
        if (mdPerms.read) {
            navigate(`/views/metadata?${ParamsSerializer(p)}`);
        }
    };

    // incoming
    useEffect(() => {
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        setParams({
            start,
            end,
            quick: props.data.quick,
            filter: props.data.settings.json_filter,
            sources: ['conn'],
        });
    }, [props.data.start, props.data.end, props.data.quick, props.data.settings]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            {mdPerms.read && <SeaUrchin
                ip={props.data.settings.ip}
                params={params}
                onFilter={handleSeaurchinFilter}
                size={props.size}
                showhelp={props.hover}
                setBusy={props.setBusy}/>}
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const daForm = useValidate({});
    const { assets, sensorNames } = useContext(DataContext);

    const [fields, setFields] = useState([]);
    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [order, setOrder] = useState(props.data.settings.order || 'Most');
    const [ip, setIp] = useState(props.data.settings.ip || '');
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const mdPerms = Auth.permissions.get('Metadata');

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleIPChange = (e, validID, valid, validated, value) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        if (!validated) setIp(value);
    };

    const handleOrderChange = e => setOrder(e.target.value);

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0) {
            return true;
        }
        return checkValidity(formState);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
                order,
                ip,
            },
        });
    };

    const fetchFields = async () => {
        if (mdPerms.read) {
            const fields = await MetadataService.cacheFields();
            setFields(fields.filter(c => c.indexed && c.type !== 'timestamp'));
        }
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box style={{ position: 'absolute' }}>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    <TextField
                        select
                        label="Order"
                        aria-label="Order"
                        value={order}
                        onChange={handleOrderChange}>
                        {['Most', 'Least'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Validator
                        style={{ width: 200 }}
                        label="IP"
                        aria-label="IP"
                        value={ip}
                        valueType="ipRange"
                        onChange={handleIPChange}
                        component={AssetSelector}/>
                </div>
                {props.data.date_override &&
                    <div className={classes.inlineForm}>
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    </div>
                }
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        className={classes.tagsButton}
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(MetadataService.toEnglish(filter, true, sensorNames, assets))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button
                        aria-label="cancel"
                        onClick={props.onCancel}
                        color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MtdtIPActivityWidget as default,
    DEFAULTS,
};
